/**
 * Description: Shared capability detail fields used in both inline and full capability forms
 */

import React from 'react'
import { UseFormRegister, FieldErrors } from 'react-hook-form'
import { Capability } from '@services/api'

interface CapabilityDetailsFieldsProps {
  register: UseFormRegister<Capability>
  errors: FieldErrors<Capability>
  compact?: boolean
}

const CapabilityDetailsFields: React.FC<CapabilityDetailsFieldsProps> = ({ register, errors, compact = false }) => {
  const gridClass = compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1 md:grid-cols-2 gap-6'
  const labelClass = compact
    ? 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
    : 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
  const statusLabelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'
  const statusGapClass = 'flex gap-6'
  const colSpan = compact ? '' : 'md:col-span-2'

  return (
    <>
      <div className={colSpan}>
        <label className={labelClass}>
          Capability Name <span className="text-red-500">*</span>
        </label>
        <input
          {...register('capability_name', { required: 'Capability name is required' })}
          className="input-field"
          placeholder="Enter capability name"
        />
        {errors.capability_name && <p className="mt-1 text-sm text-red-600">{errors.capability_name.message}</p>}
      </div>

      <div className={colSpan}>
        <label className={labelClass}>Description</label>
        <textarea
          {...register('description')}
          rows={compact ? 2 : 3}
          className="input-field"
          placeholder={compact ? 'Enter description' : 'Enter capability description'}
        />
      </div>

      <div className={`${gridClass} ${colSpan}`}>
        <div>
          <label className={labelClass}>Capability Code</label>
          <input {...register('capability_code')} className="input-field" placeholder="e.g., CAP-001" />
        </div>
        <div>
          <label className={labelClass}>Category</label>
          <input
            {...register('category')}
            className="input-field"
            placeholder={compact ? 'e.g., Monitoring' : 'e.g., Monitoring, Ticketing, Diagnostics'}
          />
        </div>
      </div>

      <div className={colSpan}>
        <label className={statusLabelClass}>Status</label>
        <div className={statusGapClass}>
          {[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'deprecated', label: 'Deprecated' },
          ].map((option) => (
            <label key={option.value} className="flex cursor-pointer items-center space-x-2">
              <input
                type="radio"
                value={option.value}
                {...register('status')}
                className="text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
            </label>
          ))}
        </div>
      </div>
    </>
  )
}

export default CapabilityDetailsFields
